import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Select, Flex, Tooltip, Typography, Spin } from 'antd';
import { useProject } from '@contexts/Project.context';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import useSetSearchParams from '@hooks/useSetSearchParams.hook';
import { getProjectCollections } from '@utils/api/collections';
import { projectCollectionsKey } from '@utils/collectionQueries';

const { Text } = Typography;

/**
 * Collection picker dropdown for selecting a collection to view detection results.
 *
 * Uses Ant Design Select component for dropdown UI.
 * Syncs selection with URL query param (?collection_uuid=xxx).
 * Stores selected collection UUID in Project.context.
 *
 * The query key comes from `projectCollectionsKey` so the writes that rename or
 * delete a collection invalidate exactly this list (see `collectionQueries`).
 */
const CollectionPicker = () => {
	const setSearchParams = useSetSearchParams();
	const [searchParams] = useSearchParams();

	const { project, collectionUuid, annotationMode, dispatch } = useProject();
	const { busy: mapBusy, reason: blockedReason } = useMapToolBusy();
	const { uuid: projectUuid } = project ?? {};

	// Fetch collections for this project
	const {
		data: collections,
		isLoading,
		error,
	} = useQuery({
		queryKey: projectCollectionsKey(projectUuid),
		queryFn: () => getProjectCollections(projectUuid),
		enabled: !!projectUuid,
		staleTime: 5 * 60 * 1000,
	});

	// Build select options. `collection_name` is guarded rather than assumed —
	// an entry missing one shows its uuid instead of rendering blank.
	const options =
		collections?.map(collection => ({
			value: collection.collection_uuid,
			label: collection.collection_name ?? collection.collection_uuid,
		})) ?? [];

	// Pick a collection (update context + URL)
	const pickCollection = collectionUuid => {
		if (!collectionUuid) {
			dispatch({ type: 'setCollectionUuid', payload: null });
			// TODO: This sets undefinde in the url
			// FIXME: The URL should be cleared, not set to undefined
			// this may cause others functionality in the project thats why need more investigate
			setSearchParams({ collection_uuid: undefined });
			return;
		}

		dispatch({ type: 'setCollectionUuid', payload: collectionUuid });
		setSearchParams({ collection_uuid: collectionUuid });
	};

	// Sync selection from the URL (the source of truth) into context.
	// Only dispatch when the URL value differs from what's already in context —
	// searchParams changes reference on every navigation, so an unguarded
	// dispatch would re-fire and churn state on unrelated URL changes.
	useEffect(() => {
		if (isLoading || !collections) return;

		const urlCollectionUuid = searchParams.get('collection_uuid');
		if (urlCollectionUuid === collectionUuid) return;

		const nextCollectionUuid = collections.some(
			collection => collection.collection_uuid === urlCollectionUuid
		)
			? urlCollectionUuid
			: null;
		dispatch({ type: 'setCollectionUuid', payload: nextCollectionUuid });
	}, [isLoading, collections, searchParams, collectionUuid, dispatch]);

	// Don't show during annotation mode
	if (annotationMode) {
		return null;
	}

	return (
		<Flex align="center" gap={8} data-tour="collection-picker">
			<Text style={{ opacity: 0.9, fontWeight: 600 }}>Analyzed Collections:</Text>

			{/* A boundary edit owns the collection it started on, and an AOI draw is
			    sketching against the collection on screen — changing it under either
			    re-targets work already in progress. useBoundaryEditor enforces the
			    edit case too; disabling the control is the affordance. */}
			<Tooltip title={blockedReason}>
				{/* antd removes pointer events from disabled controls, so the wrapper
				    keeps the tooltip hoverable while the picker is disabled. */}
				<span>
					<Select
						placeholder="Select a collection"
						value={collectionUuid}
						allowClear
						onChange={pickCollection}
						options={options}
						loading={isLoading}
						disabled={isLoading || !!error || !collections?.length || mapBusy}
						style={{ width: 275 }}
						notFoundContent={
							isLoading ? (
								<Spin size="small" />
							) : error ? (
								<Text type="danger">Failed to load collections</Text>
							) : (
								<Text type="secondary">No collections available</Text>
							)
						}
					/>
				</span>
			</Tooltip>
		</Flex>
	);
};

export default CollectionPicker;
